import apiClient from './apiClient';

export interface TestScenario {
  id: string;
  name: string;
  description: string;
  command: string;
  category: 'auth' | 'workflow' | 'platform' | 'performance' | 'accessibility';
  duration: string;
}

export interface TestExecutionRequest {
  scenarios: string[];
  config?: {
    baseUrl?: string;
    headless?: boolean;
    timeout?: number;
  };
}

export interface TestExecutionResponse {
  executionId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  message: string;
}

export interface TestProgressUpdate {
  executionId: string;
  status: 'running' | 'completed' | 'failed';
  currentScenario?: string;
  completedScenarios: number;
  totalScenarios: number;
  currentStep?: string;
  logs?: string[];
  apiCalls?: APICallLog[];
}

export interface APICallLog {
  timestamp: string;
  method: string;
  url: string;
  status: number;
  duration?: number;
  scenario?: string;
  requestBody?: any;
  responseBody?: any;
}

export interface TestResults {
  executionId: string;
  status: 'completed' | 'failed';
  startTime: string;
  endTime: string;
  duration: number;
  totalTests: number;
  passed: number;
  failed: number;
  skipped: number;
  scenarios: ScenarioResult[];
  screenshots: string[];
  reportPath?: string;
  logs: string[];
}

export interface ScenarioResult {
  scenarioId: string;
  name: string;
  status: 'passed' | 'failed' | 'skipped';
  duration: number;
  error?: string;
  screenshots: string[];
  steps: StepResult[];
}

export interface StepResult {
  name: string;
  status: 'passed' | 'failed' | 'skipped';
  duration: number;
  error?: string;
  screenshot?: string;
}

class TestExecutionService {
  private baseUrl = '/api/tests';

  /**
   * Execute test scenarios
   */
  async executeTests(request: TestExecutionRequest): Promise<TestExecutionResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/execute`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to execute tests:', error);
      throw new Error('Failed to start test execution');
    }
  }

  /**
   * Get test execution status
   */
  async getExecutionStatus(executionId: string): Promise<TestProgressUpdate> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/status/${executionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get execution status:', error);
      throw new Error('Failed to get test execution status');
    }
  }

  /**
   * Get test results
   */
  async getTestResults(executionId: string): Promise<TestResults> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/results/${executionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get test results:', error);
      throw new Error('Failed to get test execution results');
    }
  }

  /**
   * Stop test execution
   */
  async stopExecution(executionId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/stop/${executionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to stop test execution:', error);
      throw new Error('Failed to stop test execution');
    }
  }

  /**
   * Get screenshot by path
   */
  async getScreenshot(screenshotPath: string): Promise<string> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/screenshot`, {
        params: { path: screenshotPath },
        responseType: 'blob'
      });
      
      // Convert blob to base64 for display
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      console.error('Failed to get screenshot:', error);
      throw new Error('Failed to load screenshot');
    }
  }

  /**
   * Download test report
   */
  async downloadReport(executionId: string, format: 'html' | 'json' | 'junit' = 'html'): Promise<void> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/report/${executionId}`, {
        params: { format },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `test-report-${executionId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
      throw new Error('Failed to download test report');
    }
  }

  /**
   * Get available test scenarios
   */
  async getAvailableScenarios(): Promise<TestScenario[]> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/scenarios`);
      return response.data;
    } catch (error) {
      console.error('Failed to get available scenarios:', error);
      // Return default scenarios if API fails
      return this.getDefaultScenarios();
    }
  }

  /**
   * Create WebSocket connection for real-time updates
   */
  createWebSocketConnection(executionId: string): WebSocket {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/tests/ws/${executionId}`;
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('WebSocket connected for test execution:', executionId);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('WebSocket connection closed for test execution:', executionId);
    };
    
    return ws;
  }

  /**
   * Default scenarios (fallback if API is not available)
   */
  private getDefaultScenarios(): TestScenario[] {
    return [
      {
        id: 'auth-flow',
        name: 'Authentication Flow',
        description: 'Tests login, 2FA handling, and authentication redirects',
        command: 'npm run test:e2e src/test/e2e/auth.spec.ts',
        category: 'auth',
        duration: '~2 min'
      },
      {
        id: 'complete-workflow',
        name: 'Complete Job Workflow',
        description: 'Full end-to-end job creation and execution workflow',
        command: 'npm run test:e2e src/test/e2e/autopilot/complete-job-workflow.spec.ts',
        category: 'workflow',
        duration: '~5 min'
      },
      {
        id: 'platform-selection',
        name: 'Platform Selection',
        description: 'Tests platform discovery and selection interface',
        command: 'npm run test:e2e src/test/e2e/autopilot/platform-selection-discovery.spec.ts',
        category: 'platform',
        duration: '~3 min'
      },
      {
        id: 'record-selection',
        name: 'Record Selection Workflow',
        description: 'Tests data extraction and record selection process',
        command: 'npm run test:e2e src/test/e2e/autopilot/record-selection-workflow.spec.ts',
        category: 'workflow',
        duration: '~4 min'
      },
      {
        id: 'performance-audit',
        name: 'Performance Audit',
        description: 'Lighthouse performance testing and Web Vitals analysis',
        command: 'npm run test:e2e src/test/e2e/lighthouse-audit.spec.ts',
        category: 'performance',
        duration: '~3 min'
      },
      {
        id: 'accessibility-audit',
        name: 'Accessibility Audit',
        description: 'AXE accessibility testing and compliance checks',
        command: 'npm run test:e2e src/test/e2e/axe-audit.spec.ts',
        category: 'accessibility',
        duration: '~2 min'
      }
    ];
  }

  /**
   * Mock test execution for development (temporary fallback)
   */
  async mockTestExecution(scenarios: string[]): Promise<{
    executionId: string;
    mockResults: TestResults;
  }> {
    const executionId = `mock-${Date.now()}`;
    const startTime = new Date().toISOString();
    
    // Simulate some delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const mockResults: TestResults = {
      executionId,
      status: 'completed',
      startTime,
      endTime: new Date().toISOString(),
      duration: Math.random() * 300 + 60, // 1-5 minutes
      totalTests: scenarios.length,
      passed: Math.floor(Math.random() * scenarios.length) + 1,
      failed: Math.floor(Math.random() * 2),
      skipped: 0,
      scenarios: scenarios.map(scenarioId => ({
        scenarioId,
        name: this.getDefaultScenarios().find(s => s.id === scenarioId)?.name || scenarioId,
        status: Math.random() > 0.8 ? 'failed' : 'passed',
        duration: Math.random() * 120000 + 30000, // 30s - 2min
        screenshots: [`${scenarioId}-step-1.png`, `${scenarioId}-step-2.png`],
        steps: [
          {
            name: 'Setup',
            status: 'passed',
            duration: Math.random() * 5000 + 1000,
          },
          {
            name: 'Execute',
            status: Math.random() > 0.9 ? 'failed' : 'passed',
            duration: Math.random() * 60000 + 10000,
            error: Math.random() > 0.9 ? 'Element not found: button[data-testid="submit"]' : undefined
          },
          {
            name: 'Cleanup',
            status: 'passed',
            duration: Math.random() * 2000 + 500,
          }
        ]
      })),
      screenshots: scenarios.flatMap(s => [`${s}-step-1.png`, `${s}-step-2.png`]),
      logs: [
        'Starting test execution...',
        'Browser launched successfully',
        'Navigation completed',
        'Test execution completed'
      ]
    };

    return { executionId, mockResults };
  }
}

export const testExecutionService = new TestExecutionService();